"use client";

import type { RefObject } from "react";
import { Loader2, ShieldCheck } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useMountEffect } from "@/hooks/use-mount-effect";
import { cn } from "@/lib/utils";
import {
  DEFAULT_HOSTED_PAYMENT_LABELS,
  DEFAULT_VA_LABELS,
  formatPrice,
  type QrisLabels,
} from "./qris-payment-step.helpers";
import { HostedPaymentPanel } from "./hosted-payment-panel";
import { QrisPendingPanel } from "./qris-pending-panel";
import type { QrisExpiryPhase } from "./use-qris-countdown";
import {
  useVirtualAccount,
  type InitialVirtualAccount,
  type VaBank,
} from "./use-virtual-account";
import { VirtualAccountPanel } from "./virtual-account-panel";

export type PaymentRail = "va" | "qris" | "hosted";

/**
 * Bank transfer leads, and is the rail a buyer lands on.
 *
 * On a phone the QR and the banking app are on the same screen, so paying by QR
 * means screenshotting and app-switching under a countdown. A virtual account
 * is a number the buyer types into the app they already have, whenever they get
 * to it. QRIS stays one tap away, and remains the natural choice on desktop
 * where the QR is scanned with a second device.
 */
export const DEFAULT_RAIL: PaymentRail = "va";

interface PaymentRailsPanelProps {
  paymentId: string;
  claimToken?: string;
  initialVirtualAccount?: InitialVirtualAccount | null;
  rail: PaymentRail;
  onRailChange: (rail: PaymentRail) => void;
  selectedBank: VaBank;
  onSelectBank: (bank: VaBank) => void;
  labels: QrisLabels;
  amount: number;
  effectiveQr: string | null;
  invoiceUrl?: string | null;
  canvasRef: RefObject<HTMLCanvasElement | null>;
  canSimulate: boolean;
  isSimulating: boolean;
  onSimulate: () => void;
  onSaveImage: () => void;
  phase: QrisExpiryPhase;
  secondsLeft: number;
  countdownLabel: string;
  layout: "stacked" | "split";
}

/**
 * The checkout chrome: what the buyer is paying, how far along it is, and which
 * rail they are on. The rails themselves render only their own method.
 *
 * The amount, the polling status, and the security line live here rather than
 * inside each rail because they are true of the payment, not of the method.
 * Duplicating them per rail meant they moved and re-rendered when the buyer
 * switched tabs, which reads as the price changing.
 */
export function PaymentRailsPanel({
  paymentId,
  claimToken,
  initialVirtualAccount,
  rail,
  onRailChange,
  selectedBank,
  onSelectBank,
  labels,
  amount,
  layout,
  ...qrisProps
}: PaymentRailsPanelProps) {
  const va = labels.va ?? DEFAULT_VA_LABELS;
  const hosted = labels.hosted ?? DEFAULT_HOSTED_PAYMENT_LABELS;
  const railLabels: Record<PaymentRail, string> = {
    va: va.railBank,
    qris: va.railQris,
    hosted: va.railHosted,
  };

  const { accountFor, pendingBank, error, issue } = useVirtualAccount({
    paymentId,
    errorLabel: va.error,
    initialAccount: initialVirtualAccount ?? null,
    ...(claimToken ? { claimToken } : {}),
  });

  // Issue the default bank's number as soon as the buyer is on this rail, so
  // the common path shows a payable number without an extra tap.
  useMountEffect(() => {
    if (rail === "va") {
      void issue(selectedBank);
    }
  });

  const handleRailChange = (nextRail: PaymentRail) => {
    if (nextRail === "va") {
      void issue(selectedBank);
    }
    onRailChange(nextRail);
  };

  const handleSelectBank = (bank: VaBank) => {
    onSelectBank(bank);
    void issue(bank);
  };

  const availableRails = (Object.keys(railLabels) as PaymentRail[]).filter(
    (option) => option !== "hosted" || Boolean(qrisProps.invoiceUrl),
  );

  const summary = (
    <aside className="flex min-w-0 flex-col gap-4 rounded-2xl border border-border-primary bg-surface-secondary p-4 @2xl:p-5">
      <div className="min-w-0 space-y-1">
        <p className="text-xs uppercase tracking-wide text-text-muted">
          {labels.amountLabel}
        </p>
        <p className="break-words text-3xl font-bold tracking-tight text-text-heading">
          {formatPrice(amount)}
        </p>
      </div>

      <div
        className="flex min-w-0 items-center gap-2 text-sm text-text-muted"
        role="status"
        aria-live="polite"
      >
        <Loader2
          className="h-4 w-4 shrink-0 animate-spin motion-reduce:animate-none"
          aria-hidden="true"
        />
        <span className="min-w-0 break-words">{labels.statusPending}</span>
      </div>

      <div className="flex min-w-0 items-start gap-2 border-t border-border-primary pt-3 text-xs text-text-body">
        <ShieldCheck className="mt-px h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="min-w-0 break-words">{labels.secure}</span>
      </div>
    </aside>
  );

  return (
    <div className="@container w-full min-w-0">
      <div
        className="mb-5 flex w-full min-w-0 gap-1 rounded-xl bg-surface-secondary p-1"
        role="tablist"
        aria-label={labels.title}
      >
        {availableRails.map((option) => (
          <Button
            key={option}
            type="button"
            role="tab"
            aria-selected={rail === option}
            variant="ghost"
            onClick={() => handleRailChange(option)}
            className={cn(
              "min-h-11 flex-1 min-w-0 font-medium",
              rail === option
                ? "bg-background text-text-heading shadow-card hover:bg-background"
                : "text-text-muted hover:text-text-heading",
            )}
          >
            {railLabels[option]}
          </Button>
        ))}
      </div>

      <div
        className={cn(
          "grid min-w-0 items-start gap-5",
          layout === "split" &&
            "@2xl:grid-cols-[minmax(0,300px)_minmax(0,1fr)] @2xl:gap-8",
        )}
      >
        {summary}

        <div className="min-w-0">
          {rail === "va" ? (
            <VirtualAccountPanel
              labels={labels}
              selectedBank={selectedBank}
              account={accountFor(selectedBank)}
              pendingBank={pendingBank}
              error={error}
              onSelectBank={handleSelectBank}
            />
          ) : rail === "qris" ? (
            <QrisPendingPanel labels={labels} layout={layout} {...qrisProps} />
          ) : qrisProps.invoiceUrl ? (
            <HostedPaymentPanel
              labels={hosted}
              invoiceUrl={qrisProps.invoiceUrl}
              paymentId={paymentId}
              amount={amount}
              guest={Boolean(claimToken)}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}
